<template>
  <SideSheet
    :is-open="props.isOpen"
    position="right"
    container-class="screen-size"
    content-size="md"
    @close="handleClose"
  >
    <div class="sheet" data-testid="register-panel-sheet">
      <div class="sheet__header">
        <ButtonIconContextual
          data-testid="register-panel-close"
          label=""
          icon="EF0030"
          @click.native="handleClose"
        />
      </div>
      <div class="sheet__container">
        <Heading :level="2">Cadastro</Heading>
        <SpinnerLoader v-if="isLoading" class="sheet__loading" :size="48" />
        <div v-else-if="hasError" class="sheet__error">
          <div class="sheet__round-icon sheet__round-icon-warn">
            <BaseIcon icon="EF0062" width="24" />
          </div>
          <div class="sheet__error-header">
            <Heading :level="3"
              >Não foi possível carregar os dados neste momento</Heading
            >
            <BaseText class="sheet__error-disclaimer" size="md">
              Aguarde alguns instantes e tente novamente
            </BaseText>
          </div>
          <ButtonSecondary
            class="sheet__error-button"
            label="Tentar novamente"
            @click="retry"
          />
        </div>
        <DataTable
          v-else
          :columns="columns"
          :data="notificationsData"
          :status="isLoading ? 'loading' : 'default'"
          has-interaction
          @on-row-click="handleClick"
        />
      </div>
    </div>
    <VerifyEmailModal
      v-if="isOpenVerifyEmailModal"
      data-testid="verify-email-modal"
      :is-open="isOpenVerifyEmailModal"
      @close="handleToggleVerifyEmailModal"
    />
  </SideSheet>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import {
  Heading,
  ButtonIconContextual,
  BaseIcon,
  ButtonSecondary,
  SpinnerLoader,
  DataTable,
  BaseText
} from '@warrenbrasil/nebraska-web';

import { SideSheet } from '@/modules/common/components/SideSheet';
import {
  getNotifications,
  EAction,
  IRegisterNotification
} from '@/modules/home/services/register';
import { EDirection } from '@/modules/transaction/services/unified-account/enums';
import { StatementAvatarCell } from '@/modules/transaction/views/Transactions/components/StatementAvatarCell';
import VerifyEmailModal from '@/modules/home/components/HomeRegister/components/VerifyEmailModal/VerifyEmailModal.vue';
import { getDomainAndPlatform } from '@/modules/common/helpers/onboarding/getDomainAndPlatform';

const notificationsData = ref<IRegisterNotification[]>([]);
const isLoading = ref<boolean>(false);
const hasError = ref<boolean>(false);
const isEmpty = ref<boolean>(false);
const isOpenVerifyEmailModal = ref<boolean>(false);

const emit = defineEmits(['close']);

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
});

watch(
  () => props.isOpen,
  async isOpen => {
    if (isOpen) {
      retry();
    }
  }
);

const retry = async () => {
  await fetchNotifications();
};

const fetchNotifications = async () => {
  try {
    isLoading.value = true;
    hasError.value = false;
    const data = await getNotifications();
    notificationsData.value = data.registerNotifications;
    isEmpty.value = data.registerNotifications.length === 0;
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const resetCentral = () => {
  notificationsData.value = [];
  isLoading.value = false;
  hasError.value = false;
  isEmpty.value = false;
};

const handleClose = () => {
  resetCentral();
  emit('close');
};

const handleSuitability = () => {
  const { domain, platform } = getDomainAndPlatform();

  window.location.href = `/onboarding/${domain}/${platform}/suitability`;
};

const handleClick = (notification: IRegisterNotification) => {
  switch (notification.action) {
    case EAction.SUITABILITY:
      handleSuitability();
      break;
    case EAction.EMAIL:
      handleToggleVerifyEmailModal();
      break;
    default:
      break;
  }
};

const handleToggleVerifyEmailModal = () => {
  isOpenVerifyEmailModal.value = !isOpenVerifyEmailModal.value;
};

const columns = computed(() => {
  return [
    {
      width: '5%',
      component: StatementAvatarCell,
      getter: ({ icon }) => ({
        iconName: icon,
        size: 'medium',
        type: EDirection.Debit
      })
    },
    {
      width: '90%',
      getter: ({ title, description }) => ({
        text: title,
        description
      })
    },
    {
      width: '5%',
      type: 'icon',
      getter: () => ({
        icon: 'EA0170',
        ariaLabel: 'Detalhes'
      })
    }
  ];
});
</script>

<style lang="less" scoped>
.sheet {
  height: auto;
  overflow-y: auto;
  text-align: left;
  color: @text-primary;

  &__header {
    padding: @size-spacing-x300;
    height: 64px;
    width: 100%;
  }

  &__container {
    padding: @size-spacing-x1000 @size-spacing-x1200;
    width: 100%;
  }

  &__loading {
    margin: @size-spacing-x400 auto;
    display: flex;
  }

  &__empty {
    padding: @size-spacing-x2000 @size-spacing-x900;
    text-align: center;

    &-disclaimer {
      padding-top: @size-spacing-x400;
    }
  }

  &__round-icon {
    width: 64px;
    height: 64px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border-radius: 100%;
    color: @element-primary;
    background-color: @neutral-secondary;

    &-warn {
      color: @status-alert;
      background-color: @status-alert-background;
    }
  }

  &__error {
    padding: @size-spacing-x2000 @size-spacing-x900;
    text-align: center;

    &-header {
      padding-top: @size-spacing-x400;
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    &-disclaimer {
      padding-top: @size-spacing-x300;
    }

    &-button {
      margin-top: @size-spacing-x400;
    }
  }
}
</style>
